/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useEffect, useMemo, useState } from "react";

import { useClient } from "@/providers/auth";

import { apiBaseUrl } from "@/utils/remote";

import { useEnvironment } from "@/hooks/useEnvironment";

// Refresh flags every 10 minutes
const REFRESH_INTERVAL = 10 * 60 * 1000;

export enum FeatureFlagNames {
  ShowAssetIndexDevices = "ShowAssetIndexDevices",
  ShowUsersManagement = "ShowUsersManagement",
  TestingFlag1 = "TestingFlag1",
  TestingFlag2 = "TestingFlag2",
  RecaptchaTokenRequired = "recaptchaTokenRequired",
  TripIntelligence = "TripIntelligence",
  showSuzukenApprovalProcess = "showSuzukenApprovalProcess",
  /** User Guides tab, ScribeHow embed, and updated footer on the support contact page. */
  supportUserGuides = "supportUserGuides",
  AssetTimeline = "AssetTimeline",
  /** Trip timeline shows all events from trip creation through page load (not the metrics time filter). */
  TripTimelineFullRange = "TripTimelineFullRange",
  readersPage = "readersPage",
  listAssetVisibilityWithTrackingTimeRanges = "listAssetVisibilityWithTrackingTimeRanges",
  showDeviceLteRsrp = "showDeviceLteRsrp",
  NewClusterOverlayInDeckGLMap = "NewClusterOverlayInDeckGLMap",
  NewResetIconInDeckGLMap = "NewResetIconInDeckGLMap",
  NewLegendIconInDeckGLMap = "NewLegendIconInDeckGLMap",
  NewDateTimeFilterOptionsForReaders = "NewDateTimeFilterOptionsForReaders",
  IntelligenceTowers = "IntelligenceTowers",
  IntelligenceTowersAnalyticsView = "IntelligenceTowersAnalyticsView",
  IntelligenceTowersDeliveryPerformance = "IntelligenceTowerDeliveryPerformance",
  IntelligenceTowersAgentIntegration = "IntelligenceTowersAgentIntegration",
  IntelligenceTowersDiagnostics = "IntelligenceTowersDiagnostics",
  viewByPlaceFilter = "viewByPlaceFilter",
  DeckGLSensorsMap = "DeckGLSensorsMap",
  DeckGLAssetsMap = "DeckGLAssetsMap",
  TableauEmbeddedReports = "TableauEmbeddedReports",
  /** Download data menu on embedded Tableau reports. */
  TableauReportDataDownload = "TableauReportDataDownload",
  ShowStandBySensors = "ShowStandBySensors",
  viewByPlaceFilterForSensor = "viewByPlaceFilterForSensor",
  viewByPlaceFilterForAssets = "viewByPlaceFilterForAssets",
  activeTripsForDevicePage = "activeTripsForDevicePage",
  devicePageTripTimeline = "devicePageTripTimeline",
  DeckGlSensorMap = "DeckGlSensorMap",
  DeckGlAssetMap = "DeckGlAssetMap",
  ManageUserFromSummitForAdmins = "ManageUserFromSummitForAdmins",
  DeckGlAlertMap = "DeckGlAlertMap",
  filterTripsByOriginDestination = "filterTripsByOriginDestination",
  use10kPageSizeAssetsLocations = "use10kPageSizeAssetsLocations",
  SensorLocationDashboard = "SensorLocationDashboard",
  /** Dropdown-based “recently heard” filter on the assets list (matches readers UX). */
  assetsDynamicRecentlyHeard = "assetsDynamicRecentlyHeard",
  /** Dropdown-based “recently heard” filter on the sensors (devices) list. */
  sensorsDynamicRecentlyHeard = "sensorsDynamicRecentlyHeard",
  viewByPlaceFilterForMapFeatures = "viewByPlaceFilterForMapFeatures",
  viewByGeofenceFilterForMapFeatures = "viewByGeofenceFilterForMapFeatures",
  TripIntelligenceTowerMarketing = "TripIntelligenceTowerMarketing",
  showNewMapFeaturesMapDesign = "showNewMapFeaturesMapDesign",
  NewDateTimeRangePicker = "NewDateTimeRangePicker",
  viewByPlaceFilterRadius = "viewByPlaceFilterRadius",
  MarketingTowerDataAgent = "MarketingTowerDataAgent",
  NewDeckGLMapIcons = "NewDeckGLMapIcons",
  seenByHistoryForDevice = "seenByHistoryForDevice",
  ShowNewAttributeDesign = "ShowNewAttributeDesign",
  NewDeckGLMapHoveringEffect = "NewDeckGLMapHoveringEffect",
  /**
   * Deferred, batched query-param processing for Summit list-page filters on
   * mobile: mounts a single filter panel per breakpoint and commits all filter
   * changes in one URL update on "Apply Filters"/"Reset". When disabled, the
   * legacy behavior (both panels mounted, immediate per-filter URL writes) is
   * used.
   */
  NewSummitFilterBatchProcessing = "NewSummitFilterBatchProcessing",
  DeviceIdFilterForAssetHistoryLocations = "DeviceIdFilterForAssetHistoryLocations",
}

type FeatureFlags = {
  isEnabled: (flag: FeatureFlagNames) => boolean;
  loading: boolean;
};

type FeatureFlagsContextProps = {
  enabledFlags: Set<string>;
  loading: boolean;
};

const FeatureFlagsContext = createContext<FeatureFlagsContextProps | undefined>(
  undefined,
);

function areSetsEqual(a: Set<string>, b: Set<string>) {
  if (a.size !== b.size) {
    return false;
  }
  return [...a].every((value) => b.has(value));
}

export const FeatureFlagsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const client = useClient();
  const [enabledFlags, setEnabledFlags] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchFlags = async () => {
      try {
        const resp = await client.get("/v1/featureFlags:list", {
          baseURL: apiBaseUrl(),
        });
        if (cancelled) return;

        const flags: Set<string> = new Set(resp.data.enabledFeatureFlagNames);

        // No need re-render the component if no changes on flags.
        setEnabledFlags((prev) => (areSetsEqual(prev, flags) ? prev : flags));
      } catch (error) {
        if (!cancelled) {
          console.error(`Failed to fetch feature flags: ${error}`);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchFlags();

    const intervalId = setInterval(() => {
      fetchFlags();
    }, REFRESH_INTERVAL);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [client]);

  // Memoize the context value to prevent unnecessary re-renders
  const featureFlagContextValue = useMemo(
    () => ({ enabledFlags, loading }),
    [enabledFlags, loading],
  );

  return (
    <FeatureFlagsContext.Provider value={featureFlagContextValue}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export function useFeatureFlags(): FeatureFlags {
  const context = React.useContext(FeatureFlagsContext);
  const { isDev } = useEnvironment();
  if (context === undefined) {
    if (isDev) {
      // In development: throw error to crash and alert developer
      throw new Error(
        `useFeatureFlags must be used within a FeatureFlagsProvider`,
      );
    } else {
      // In production: log error but return safe defaults
      console.error(
        `useFeatureFlags called outside a FeatureFlagsProvider. All feature flags will return false.`,
      );

      // Return safe fallback that disables all features
      return {
        isEnabled: () => false,
        loading: false,
      };
    }
  }
  const { enabledFlags, loading } = context;
  const isEnabled = (flag: FeatureFlagNames): boolean => {
    return enabledFlags.has(flag);
  };
  return { isEnabled, loading };
}
